import { find, findIndex, map, merge, reduce, sumBy } from 'lodash';
import { PackingListProductModel } from '@core/app/scripts/classes/packing-list-product-model';
import { PackingListProductSkuModel } from '@core/app/scripts/classes/packing-list-product-sku-model';

export type PackingListStatus =
  | 'complete'
  | 'complete-and-discard'
  | 'complete-and-move'
  | 'incomplete'
  | 'ready-for-picking'
  | 'waiting'
  | 'picking'
  | 'ready-for-shipping'
  | 'shipping'
  | 'ready-for-qa'
  | 'qa';

/**
 * Class for PackingList. Related to {@link SalesOrderModel}
 */
export class PackingListModel {
  _id: string;
  orderPicker: string;

  customer: {
    name: string;
    _id: string;
  };

  created: {
    date: Date;
  };

  status: PackingListStatus;
  status_history: {
    user: string;
    status: PackingListStatus;
    date: string;
  }[];

  products: PackingListProductModel[];
  numberOfParcels: number;
  reservationDate?: Date;

  // Array of parcels registered for shipment at Sendcloud
  parcels?: {
    id: number;
    tracking_number: string;
    tracking_url: string;
    collo_nr: number;
    colli_tracking_number: string;
  };

  constructor(originalValue?: any) {
    merge(
      this,
      {
        products: [],
        numberOfParcels: 0,
      },
      originalValue,
    );

    this.products = map(
      this.products,
      (product) => new PackingListProductModel(product),
    );
  }

  /**
   * Count all units left to be picked
   * @return { number }
   * missing units
   */
  countLeft() {
    return reduce(
      this.products,
      (sum, product) => {
        return (
          sum +
          sumBy(product.skus, 'qty') -
          sumBy(product.skus, (sku) => {
            return sku.picked ? sku.picked : 0;
          })
        );
      },
      0,
    );
  }

  /**
   * Add provided barcode to the packingList picked count
   * @param barcode product barcode
   * @param quantity count
   * @return
   * returns string error in case of error or productId/articleCode on success
   */
  add(
    barcode: string,
    quantity: number,
  ): string | 'unknown' | 'quantity_exceeded' {
    const foundProduct = <PackingListProductModel>(
      find(this.products, (product) => {
        return find(product.skus, { barcode });
      })
    );

    if (foundProduct) {
      const foundSku = <PackingListProductSkuModel>(
        find(foundProduct.skus, { barcode })
      );

      if (!foundSku?.qty) {
        return 'quantity_exceeded';
      }

      if (!foundSku?.picked) {
        foundSku.picked = 0;
      }

      if (foundSku.picked + quantity < 0) {
        foundSku.picked = 0;
        return foundProduct.articleCode;
      }

      if (foundSku.qty - foundSku.picked >= quantity) {
        foundSku.picked += quantity;

        // TODO LOG-160 update unit test
        return foundProduct.articleCode;
      } else {
        return 'quantity_exceeded';
      }
    } else {
      return 'unknown';
    }
  }

  /**
   * If entity can transition to status 'canceled'
   * @return
   * returns true for status 'waiting' or 'ready-for-picking', false otherwise.
   */
  canBeCancelled(): boolean {
    return this.status === 'ready-for-picking' || this.status === 'waiting';
  }

  private pickingStartTime: Date;

  /**
   * If entity was in picking state, it returns when the transtion happend
   * @return
   * The last time when it was moved to picking.
   */
  getPickingStartTime(): Date {
    const update = find(this.status_history, { status: 'picking' });

    if (update) {
      const newDate = new Date(update.date);

      if (
        !angular.isDate(this.pickingStartTime) ||
        this.pickingStartTime.toJSON() !== newDate.toJSON()
      ) {
        this.pickingStartTime = newDate;
      }

      return this.pickingStartTime;
    } else {
      return undefined;
    }
  }

  private pickingEndTime: Date;

  /**
   * If entity was in picking state and left it, the last time of leaving.
   * @return
   * The last time when it was moved away from picking.
   */
  getPickingEndTime(): Date {
    const updateIndex = findIndex(this.status_history, { status: 'picking' });

    if (updateIndex > 0) {
      const newDate = new Date(this.status_history[updateIndex - 1].date);

      if (
        !angular.isDate(this.pickingEndTime) ||
        this.pickingEndTime.toJSON() !== newDate.toJSON()
      ) {
        this.pickingEndTime = newDate;
      }

      return this.pickingEndTime;
    } else {
      return undefined;
    }
  }
}

// Hack! It makes sure that in the test scripts & test are using the same
// reference to the class
export const PackingListModelProvider = {
  provide: 'PackingListModel',
  useValue: PackingListModel,
};
